/**
 * Builder Session Utilities
 * 
 * Helper utilities for external builder integration.
 * Session tokens are created via tRPC: trpc.builder.createSession
 */

/**
 * Build external editor URL with session token
 * 
 * @param baseUrl - Base editor URL (with {{variables}})
 * @param params - Parameters for URL substitution
 * @param sessionToken - Builder session token (from trpc.builder.createSession)
 * @returns Complete URL with session token
 * 
 * @example
 * ```ts
 * // Create session via tRPC
 * const session = await trpc.builder.createSession.mutateAsync({
 *   pluginId: 'plg_abc',
 *   deploymentId: 'dep_xyz',
 * });
 * 
 * // Build URL with session token
 * const url = buildExternalEditorUrl(
 *   editorConfig.url,
 *   { pluginId, pageId, mode: 'edit' },
 *   session.token
 * );
 * window.open(url, '_blank');
 * ```
 */
export function buildExternalEditorUrl(
  baseUrl: string,
  params: {
    pluginId?: string;
    pageId?: string;
    pagePath?: string;
    deploymentId?: string;
    returnUrl?: string;
    mode?: 'edit' | 'new-page' | 'create';
    storeId?: string;
    funnelId?: string;
    stepType?: string;
  },
  sessionToken: string
): string {
  // Perform variable substitution
  let url = baseUrl
    .replace('{{pluginId}}', params.pluginId || '')
    .replace('{{pageId}}', params.pageId || '')
    .replace('{{pagePath}}', params.pagePath || '')
    .replace('{{deploymentId}}', params.deploymentId || '')
    .replace('{{returnUrl}}', params.returnUrl || '');
  
  // Parse URL and add session token
  const urlObj = new URL(url);
  urlObj.searchParams.set('sessionToken', sessionToken);
  
  // Add mode if specified
  if (params.mode) {
    urlObj.searchParams.set('mode', params.mode);
  }
  
  // Add returnUrl if specified (for "Save & Return" functionality)
  if (params.returnUrl) {
    urlObj.searchParams.set('returnUrl', params.returnUrl);
  }
  
  // Add additional context params for create mode
  if (params.storeId) {
    urlObj.searchParams.set('storeId', params.storeId);
  }
  if (params.funnelId) {
    urlObj.searchParams.set('funnelId', params.funnelId);
  }
  if (params.stepType) {
    urlObj.searchParams.set('stepType', params.stepType);
  }
  
  return urlObj.toString();
}
